/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo, memo, lazy, Suspense } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
  useWatch,
} from "react-hook-form";
import SelectInput from "../../../components/Select";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import { GULF_COUNTRIES, parseNominatimResult, FIELD_LABELS, getCountryAddressConfig } from "../../OrderManagement/_components/GulfMapPicker";
import { useDistrictFetchData } from "../../../apis/locationApis";
import { useLanguage } from "../../../context/LanguageContext";

// Lazy-load the heavy map component
const GulfMapPicker = lazy(() => import("../../OrderManagement/_components/GulfMapPicker"));

const API_BASE = "https://track-map.gwarm.net/api/areas";

interface WarehouseAddressPickerProps {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
  clearErrors: UseFormClearErrors<any>;
  control: Control<any>;
  countries: any[];
  cities: any[];
  onCountryChange?: (country: any) => void;
  onCityChange?: (city: any) => void;
  onDistrictChange?: (district: any) => void;
  selectedCountry: any;
  selectedCity: any;
  selectedDistrict: any;
}

const Spinner = memo(() => (
  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
  </svg>
));

const MapSkeleton = () => (
  <div style={{ height: 260, borderRadius: 12, border: "1px solid #e5e7eb", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <span style={{ color: "#9ca3af", fontSize: 13 }}>Loading map…</span>
  </div>
);

const WarehouseAddressPicker: React.FC<WarehouseAddressPickerProps> = ({
  register,
  errors,
  setValue,
  control,
  clearErrors,
  countries = [],
  cities = [],
  onCountryChange,
  onCityChange,
  onDistrictChange,
  selectedCountry,
  selectedCity,
  selectedDistrict,
}) => {
  const { t } = useLanguage();
  const [showMap, setShowMap] = useState(false);
  const [pinPosition, setPinPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [snappedPosition, setSnappedPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [shortAddressLoading, setShortAddressLoading] = useState(false);
  const [shortAddressError, setShortAddressError] = useState<string>("");
  const [shortAddressWarning, setShortAddressWarning] = useState<string>("");
  const [searchRef, setSearchRef] = useState<string>("");

  const countryName = selectedCountry?.name || "";
  const cityId = selectedCity?.id;

  const countryCode = useMemo(
    () => GULF_COUNTRIES.find(c => c.name.toLowerCase() === countryName?.toLowerCase())?.code || "OTHER",
    [countryName]
  );

  const addressConfig = useMemo(() => getCountryAddressConfig(countryCode), [countryCode]);

  const {
    data: districtData = [],
    isPending: isDistrictFetchPending,
    refetch: fetchDistricts,
  } = useDistrictFetchData(cityId, searchRef, "map");

  const handleDistrictSearch = useCallback((query: string) => {
    if (query.trim().length >= 3) {
      setSearchRef(query);
      fetchDistricts();
    }
  }, [fetchDistricts]);

  const applyAddressFromApi = useCallback((addr: any) => {
    // Fill full address (location)
    const fullAddr = `${addr.buildingNumber || ""} ${addr.street || ""} ${addr.district || ""} ${addr.city || ""}`.trim();
    if (fullAddr) setValue("location", fullAddr, { shouldValidate: true });

    // Handle City - prioritize matching by name in the available list
    if (addr.city) {
      const matchedCity = cities?.find((c: any) => c.name.toLowerCase() === addr.city?.toLowerCase());
      if (matchedCity) {
        if (onCityChange) onCityChange(matchedCity);
        setValue("city", matchedCity.name, { shouldValidate: true });
      } else {
        if (onCityChange) onCityChange({ name: addr.city, id: "" });
        setValue("city", addr.city, { shouldValidate: true });
      }
    } else {
      // Clear if not provided by API
      // setValue("city", "");
      // if (onCityChange) onCityChange(null);
    }

    // Handle District
    if (addr.district) {
      const matchedDist = districtData?.find((d: any) => d.name.toLowerCase() === addr.district?.toLowerCase());
      if (matchedDist) {
        if (onDistrictChange) onDistrictChange(matchedDist);
        setValue("district", matchedDist.name, { shouldValidate: true });
      } else {
        if (onDistrictChange) onDistrictChange({ name: addr.district, id: "" });
        setValue("district", addr.district, { shouldValidate: true });
      }
    } else {
      // If API doesn't provide district, but provides location text, 
      // we might want to let the user know or leave it for manual entry.
    }

    if (addr.postalCode) setValue("zipCode", addr.postalCode, { shouldValidate: true });
  }, [cities, districtData, setValue, onCityChange, onDistrictChange]);

  const watchShortAddress = useWatch({ control, name: "shortAddress" });
  const watchLocation = useWatch({ control, name: "location" });
  const watchLatitude = useWatch({ control, name: "latitude" });
  const watchLongitude = useWatch({ control, name: "longitude" });

  useEffect(() => {
    if (watchLatitude && watchLongitude) {
      const lat = parseFloat(watchLatitude);
      const lng = parseFloat(watchLongitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setPinPosition((prev) => {
          if (prev && prev.lat === lat && prev.lng === lng) return prev;
          return { lat, lng };
        });
      }
    }
  }, [watchLatitude, watchLongitude]);

  const shortAddressOk = !shortAddressLoading && !shortAddressError && watchShortAddress?.length === 8 && watchLocation;
  
  const handleMapPick = useCallback(async (latLng: { lat: number; lng: number }, mapData: any) => {
    setPinPosition(latLng);
    setSnappedPosition(null);
    setValue("latitude", latLng.lat.toString());
    setValue("longitude", latLng.lng.toString());

    let activeCountryCode = countryCode;
    const detectedCode = mapData?.address?.country_code?.toUpperCase();
    
    if (detectedCode && detectedCode !== countryCode && GULF_COUNTRIES.some(c => c.code === detectedCode)) {
      activeCountryCode = detectedCode;
      const gulfName = GULF_COUNTRIES.find(c => c.code === detectedCode)?.name || "";
      const matchedCountry = countries.find((c: any) => c.name.toLowerCase() === gulfName.toLowerCase());
      if (matchedCountry && onCountryChange) {
        onCountryChange(matchedCountry);
        setValue("country", matchedCountry.name);
      }
    }

    if (activeCountryCode === "SA") {
      setShortAddressLoading(true);
      setShortAddressError("");
      setShortAddressWarning("");
      setValue("shortAddress", "");

      // 1. Check if google map (Nominatim) data contains short address
      let foundShortAddress: string | null = null;
      if (mapData) {
        const textToSearch = JSON.stringify(mapData);
        const match = textToSearch.match(/\b([a-zA-Z]{4}\d{4})\b/);
        if (match) {
          foundShortAddress = match[1].toUpperCase();
        }
      }

      if (foundShortAddress) {
        setValue("shortAddress", foundShortAddress);

        try {
          const checkRes = await fetch(`${API_BASE}/adrress-get-by-short-address?shortAddress=${foundShortAddress}`);
          const checkData = await checkRes.json();
          if (checkData?.success && checkData?.data) {
            const shortAddr = checkData.data;
            applyAddressFromApi(shortAddr);

            if (shortAddr.latitude && shortAddr.longitude) {
              const pos = { lat: parseFloat(shortAddr.latitude), lng: parseFloat(shortAddr.longitude) };
              setPinPosition(pos);
              setSnappedPosition(pos);
              setValue("latitude", shortAddr.latitude.toString());
              setValue("longitude", shortAddr.longitude.toString());
            }
            setShortAddressWarning("");
            setShortAddressError("");
          } else {
            setShortAddressError("Short Address verified lookup failed. Please type it manually.");
          }
        } catch (err) {
          console.error("Short address verification lookup failed:", err);
          setShortAddressError("Could not verify short address. Please try again.");
        } finally {
          setShortAddressLoading(false);
        }
        return;
      }

      // 2. Check coordinates API
      try {
        const res = await fetch(`${API_BASE}/find-by-coordinates?lat=${latLng.lat}&lon=${latLng.lng}`);
        const apiData = await res.json();

        if (apiData?.success && apiData?.data) {
          const addr = apiData.data;
          const source: string = addr.source ?? "database";
          applyAddressFromApi(addr);

          if (source === "redis_geo" || source === "database_zone") {
            if (addr.latitude && addr.longitude) {
              const snapped = { lat: parseFloat(addr.latitude), lng: parseFloat(addr.longitude) };
              setSnappedPosition(snapped);
              setPinPosition(snapped);
              setValue("latitude", addr.latitude.toString());
              setValue("longitude", addr.longitude.toString());
            }
          }

          const resolvedShortCode = addr.name;
          if (resolvedShortCode) {
            setValue("shortAddress", resolvedShortCode);

            try {
              const checkRes = await fetch(`${API_BASE}/adrress-get-by-short-address?shortAddress=${resolvedShortCode}`);
              const checkData = await checkRes.json();
              if (checkData?.success && checkData?.data) {
                const shortAddr = checkData.data;
                applyAddressFromApi(shortAddr);

                if (shortAddr.latitude && shortAddr.longitude) {
                  const pos = { lat: parseFloat(shortAddr.latitude), lng: parseFloat(shortAddr.longitude) };
                  setPinPosition(pos);
                  setSnappedPosition(pos);
                  setValue("latitude", shortAddr.latitude.toString());
                  setValue("longitude", shortAddr.longitude.toString());
                }

                if (source === "database_zone") {
                  setShortAddressWarning("📍 Approximate match. Pin again for precise address.");
                } else {
                  setShortAddressWarning("");
                }
                setShortAddressError("");
              } else {
                setShortAddressError("Short Address verified lookup failed. Please type it manually.");
              }
            } catch (err) {
              console.error("Short address verification lookup failed:", err);
              setShortAddressError("Could not verify short address. Please try again.");
            }
          } else {
            setValue("shortAddress", "");
            setShortAddressError("Short Address was not found from the map. Please fill it manually.");
          }
          setShortAddressLoading(false);
          return;
        }
      } catch (err) {
        console.error("Map pick API error:", err);
      }

      // 3. Fallback: geocoding but no short address found
      setShortAddressLoading(false);
      setValue("shortAddress", "");
      setShortAddressWarning("");
      setShortAddressError("Short Address was not found from the map. Please fill it manually.");
    }

    const parsed: any = parseNominatimResult(mapData, activeCountryCode);
    applyAddressFromApi(parsed);
  }, [countryCode, countries, onCountryChange, setValue, applyAddressFromApi, countryName]);

  const handleShortAddressChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    setValue("shortAddress", val);
    setShortAddressWarning("");
    setShortAddressError("");

    if (val.length === 8) {
      setShortAddressLoading(true);
      try {
        const res = await fetch(`${API_BASE}/adrress-get-by-short-address?shortAddress=${val}`);
        const apiData = await res.json();
        if (apiData?.success && apiData?.data) {
          const addr = apiData.data;
          applyAddressFromApi(addr);
          if (addr.latitude && addr.longitude) {
            const pos = { lat: parseFloat(addr.latitude), lng: parseFloat(addr.longitude) };
            setPinPosition(pos);
            setSnappedPosition(pos);
            setValue("latitude", addr.latitude.toString());
            setValue("longitude", addr.longitude.toString());
          }
        } else {
          setShortAddressError("Short code not found.");
        }
      } catch (err) {
        setShortAddressError("Verification failed.");
      } finally {
        setShortAddressLoading(false);
      }
    }
  }, [setValue, applyAddressFromApi]);

  const isSaudiArabia = countryCode === "SA";

  return (
    <div className="col-span-full space-y-4">
      {/* Map Toggle */}
      <div className="flex justify-between items-center bg-white/50 p-3 rounded-2xl border border-[#D8D8D8]">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          {t("Pin Warehouse Location")}
          {!!pinPosition && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">📍 {t("Pinned")}</span>}
        </label>
        <button
          type="button"
          onClick={() => setShowMap(!showMap)}
          className="px-4 py-1.5 rounded-full bg-[#4F6EF7] text-white text-xs font-semibold hover:bg-[#3d59c9] transition-colors"
        >
          {showMap ? t("Hide Map") : t("Open Map")}
        </button>
      </div>

      {showMap && (
        <Suspense fallback={<MapSkeleton />}>
          <GulfMapPicker
            country={countryCode}
            onPick={handleMapPick}
            pinPosition={pinPosition}
            snappedPosition={snappedPosition}
          />
        </Suspense>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* 1. Country (Full Width or part of row) */}
        <div className="col-span-2 sm:col-span-1">
          <SelectInput
            name="country"
            label="Country"
            placeholder="Select Country"
            selectOptions={countries.map(c => ({ id: c.id, value: c.name, text: c.name }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: "Country is required" }}
            error={errors.country}
            value={selectedCountry?.name}
            setValue={(val: string) => {
              const matched = countries.find(c => c.name === val);
              if (matched && onCountryChange) onCountryChange(matched);
              setValue("country", val);
              setValue("city", "");
              setValue("district", "");
              if (onCityChange) onCityChange(null);
              if (onDistrictChange) onDistrictChange(null);
            }}
          />
        </div>

        {/* 2. Short Address (Next to Country if there's space, or below) */}
        {addressConfig.hasShortAddress && (
          <div className="col-span-2 sm:col-span-1">
            <div className="space-y-1 relative">
              <label className="text-sm font-medium text-gray-700">
                {t("Short Address (National Address)")}{isSaudiArabia && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              <div className="relative">
                <input
                  {...(() => {
                    const { onChange: formOnChange, ...rest } = register("shortAddress", {
                      required: isSaudiArabia ? "Short address is required" : false,
                      validate: (val) => {
                        if (isSaudiArabia && val && val.length !== 8) {
                          return "Short address must be 8 characters";
                        }
                        return true;
                      }
                    });
                    return {
                      ...rest,
                      onChange: (e: any) => { formOnChange(e); handleShortAddressChange(e); }
                    };
                  })()}
                  placeholder={t("e.g. RHOO2375")}
                  className={`w-full px-4 py-2 text-sm rounded-full outline-none bg-white border pr-10 ${
                    shortAddressError ? "border-red-400 bg-red-50" : 
                    shortAddressOk ? "border-green-400 bg-green-50" :
                    "border-[#D8D8D8]"
                  } focus:border-[#4F6EF7]`}
                />
                <div className="absolute inset-y-0 right-3 flex items-center gap-1">
                  {shortAddressLoading && <Spinner />}
                  {!shortAddressLoading && shortAddressError && <span className="text-red-500 text-base">✕</span>}
                  {shortAddressOk && <span className="text-green-500 text-base font-bold">✓</span>}
                </div>
              </div>
              {shortAddressError && <span className="text-xs text-red-500">{shortAddressError}</span>}
              {shortAddressWarning && <span className="text-xs text-amber-600 font-medium">{shortAddressWarning}</span>}
              {errors.shortAddress && !shortAddressError && <FormErrorMessage error={errors.shortAddress} />}
            </div>
          </div>
        )}

        {/* 3. City & District (Row) */}
        <div className="col-span-1">
          <SelectInput
            name="city"
            label={addressConfig.cityLabel}
            placeholder={addressConfig.cityPlaceholder}
            selectOptions={cities.map(c => ({ id: c.id, value: c.name, text: c.name }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: `${addressConfig.cityLabel} is required` }}
            error={errors.city}
            value={selectedCity?.name}
            disabled={false} // Don't disable to allow entering custom values
            allowCustom={true}
            setValue={(val: string) => {
              const matched = cities.find(c => c.name === val);
              if (matched) {
                if (onCityChange) onCityChange(matched);
              } else {
                if (onCityChange) onCityChange({ name: val, id: "" });
              }
              setValue("city", val);
              setValue("district", "");
              if (onDistrictChange) onDistrictChange(null);
            }}
          />
        </div>

        <div className="col-span-1">
          <SelectInput
            name="district"
            label={addressConfig.districtLabel}
            placeholder={addressConfig.districtPlaceholder}
            selectOptions={districtData.map((d: any) => ({ id: d.id, value: d.name, text: d.name }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: `${addressConfig.districtLabel} is required` }}
            error={errors.district}
            value={selectedDistrict?.name}
            disabled={false}
            onSearch={handleDistrictSearch}
            isPending={isDistrictFetchPending}
            allowCustom={true}
            setValue={(val: string) => {
              const matched = districtData.find((d: any) => d.name === val);
              if (matched) {
                if (onDistrictChange) onDistrictChange(matched);
              } else {
                if (onDistrictChange) onDistrictChange({ name: val, id: "" });
              }
              setValue("district", val);
            }}
          />
        </div>

        {/* 4. Zip Code & Full Address */}
        <div className="col-span-1">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">{t(addressConfig.zipLabel)}</label>
            <input
              {...register("zipCode", addressConfig.zipRequired ? {
                required: `${t(addressConfig.zipLabel)} ${t("is required")}`,
                pattern: {
                  value: /^\d+$/,
                  message: `${t(addressConfig.zipLabel)} ${t("must contain only digits")}`,
                }
              } : undefined)}
              placeholder={t(addressConfig.zipPlaceholder)}
              className="w-full px-4 py-2 text-sm rounded-full outline-none bg-white border border-[#D8D8D8] focus:border-[#4F6EF7]"
            />
            {errors.zipCode && <FormErrorMessage error={errors.zipCode} />}
          </div>
        </div>

        <div className="col-span-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">{t("Address Line 1")}</label>
            <textarea
              {...register("location", { required: t("Address is required") })}
              placeholder={t("e.g. Main Street, Area 51")}
              rows={2}
              className="w-full px-4 py-3 text-sm rounded-2xl outline-none bg-white border border-[#D8D8D8] focus:border-[#4F6EF7] resize-none"
            />
            {errors.location && <FormErrorMessage error={errors.location} />}
          </div>
        </div>

        {/* Latitude & Longitude (Hidden or Read-only) */}
        <input type="hidden" {...register("latitude")} />
        <input type="hidden" {...register("longitude")} />
      </div>
    </div>
  );
};

export default memo(WarehouseAddressPicker);
